import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Tooltip,
  Stack,
  Divider,
  Skeleton,
} from '@mui/material';
import config from '../../Config';
import TodayIcon from '@mui/icons-material/Today';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CancelIcon from '@mui/icons-material/Cancel';
import PaidIcon from '@mui/icons-material/Paid';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/getDashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setDashboardData(data);
    } catch (error) {
      // console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const formatCurrency = (value = 0) =>
    `£${value?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const formatCountCurrency = (count = 0, sum = 0) => {
    const paddedCount = count.toString().padStart(2, ' ');
    return `${paddedCount} (${formatCurrency(sum)})`;
  };

  const StatCard = ({ title, value, icon, color, tooltip, loadingCard }) => (
    <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
      <Tooltip title={tooltip || ''} arrow>
        <Paper
          elevation={3}
          sx={{
            height: 140,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            px: 3,
            py: 2,
            borderRadius: 3,
            borderLeft: `6px solid ${color}`,
            transition: 'transform 0.2s',
            overflow: 'hidden',
            '&:hover': { transform: 'scale(1.02)' },
          }}
        >
          {loadingCard ? (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ width: '100%', height: '100%' }}
            >
              <Skeleton
                variant="circular"
                width={36}
                height={36}
                sx={{ bgcolor: '#e0e0e0', flexShrink: 0 }}
              />
              <Stack
                direction="column"
                spacing={1}
                sx={{ flex: 1, justifyContent: 'center', width: '100%' }}
              >
                <Skeleton variant="text" width="120px" height={20} sx={{ bgcolor: '#e0e0e0' }} />
                <Skeleton variant="text" width="90%" height={28} sx={{ bgcolor: '#e0e0e0' }} />
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%' }}>
              <Box sx={{ color, fontSize: 36, width: 48, textAlign: 'center', flexShrink: 0 }}>
                {icon}
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  noWrap
                  sx={{ fontSize: 14, userSelect: 'none' }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  noWrap
                  sx={{
                    fontSize: 18,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    userSelect: 'text',
                  }}
                  title={value}
                >
                  {value}
                </Typography>
              </Box>
            </Stack>
          )}
        </Paper>
      </Tooltip>
    </Grid>
  );

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold" mb={3}>
        Dashboard
      </Typography>

      <Typography variant="h6" fontWeight="medium" mb={2} color="textSecondary">
        Booking Stats
      </Typography>
      <Grid container spacing={3} mb={4} alignItems="stretch">
        <StatCard
          title="Today's Bookings"
          value={dashboardData?.todaysBookings || 0}
          icon={<TodayIcon />}
          color="#2196f3"
          tooltip="Number of bookings made today"
          loadingCard={loading}
        />
        <StatCard
          title="Upcoming Bookings"
          value={dashboardData?.upcomingBookings || 0}
          icon={<EventAvailableIcon />}
          color="#4caf50"
          tooltip="Bookings scheduled for the future"
          loadingCard={loading}
        />
        <StatCard
          title="Cancelled Bookings"
          value={dashboardData?.cancelledBookings || 0}
          icon={<CancelIcon />}
          color="#f44336"
          tooltip="Bookings that were cancelled"
          loadingCard={loading}
        />
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" fontWeight="medium" mb={2} color="textSecondary">
        Financial Stats
      </Typography>
      <Grid container spacing={3} alignItems="stretch">
        <StatCard
          title="Total Earned"
          value={formatCurrency(dashboardData?.totalEarned || 0)}
          icon={<AccountBalanceWalletIcon />}
          color="#ff9800"
          tooltip="Total revenue earned from bookings"
          loadingCard={loading}
        />
        <StatCard
          title="Paid Bookings"
          value={formatCountCurrency(
            dashboardData?.paidBookings?.count,
            dashboardData?.paidBookings?.sum
          )}
          icon={<PaidIcon />}
          color="#009688"
          tooltip="Count and total of paid bookings"
          loadingCard={loading}
        />
        <StatCard
          title="Unpaid Bookings"
          value={formatCountCurrency(
            dashboardData?.unpaidBookings?.count,
            dashboardData?.unpaidBookings?.sum
          )}
          icon={<MoneyOffIcon />}
          color="#ff5722"
          tooltip="Count and total of unpaid bookings"
          loadingCard={loading}
        />
      </Grid>
    </Box>
  );
}
